import { PROMO_CODE_STATUS_LABELS, derivePromoCodeStatus } from './promoCodes';

// Normalizes the campaign-service eligibility response into the shape the
// Campaigns tab renders. The pro-facing `/v1/eligibilities` returns snake_case
// while GOTU-7046 declares camelCase JSON tags for the admin route, so every
// field accepts both spellings rather than betting on one — the same hedge
// `domain/Promotions/mappers.js` already makes.

// Shown wherever a value is absent. A blank cell reads as a rendering bug to
// CS; an em dash reads as "the service didn't send one".
export const EMPTY_VALUE = '—';

// `first_shift_completed` and `shift_completed` are the two trigger values
// mobile's CampaignKey ships, and the design shows the same copy on both.
const TRIGGER_TYPE_LABELS = {
  first_shift_completed: 'Complete 1 Shift',
  shift_completed: 'Complete 1 Shift',
};

// `paused` is not in the design (which only defines Live/Closed), but folding
// it into "Closed" would tell CS a running campaign had ended — the one
// question this tab exists to answer. It gets its own label. See GOTU-7054
// open question #1 if design later rules otherwise.
//
// `closing` (GOTU-8216) is the same call again: it admits nobody new but still
// pays the cohort it captured and can be reopened, so it is neither Live nor
// Closed. "Closing" is deliberately one word off "Closed" — it is on its way
// there, not there yet.
const CAMPAIGN_STATUS_LABELS = {
  active: 'Live',
  paused: 'Paused',
  closing: 'Closing',
  ended: 'Closed',
};

// The endpoint only derives `eligible` / `completed` today. The story's other
// four pro states (Application Pending, Shift Confirmed, Cancelled, Locked —
// Campaign Ended) are not on the wire and need a campaign-service change
// first; the fallback below renders them readably if they ever arrive.
const PRO_STATUS_LABELS = {
  eligible: 'Eligible',
  completed: 'Completed',
};

// "first_shift_completed" -> "First Shift Completed". The fallback for enum
// values we have not seen: a campaign added server-side stays readable here
// without an Admin Panel release, which is the point of AC 4.
const titleCaseToken = (value) =>
  String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const labelFor = (labels, value) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;

  return labels[value] ?? titleCaseToken(value);
};

// "Tue, Aug 18, 2026", per the design. Deliberately not ProPrograms'
// formatPromotionDate, which renders "Tue, August 18th, 2026" — long month
// plus ordinal is a different format. Renders in the admin's local timezone,
// the same as every other date on the profile.
export const formatCampaignDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// One eligibility row. `amount` is on the wire but has no column in the
// design, so it is deliberately not carried through.
export const mapProfessionalCampaign = (eligibility = {}) => {
  const triggerType = eligibility.trigger_type ?? eligibility.triggerType ?? null;
  const matchedAt = eligibility.matched_at ?? eligibility.matchedAt ?? null;
  const status = eligibility.status ?? null;
  const proStatus = eligibility.pro_status ?? eligibility.proStatus ?? null;

  return {
    campaignId: eligibility.campaign_id ?? eligibility.campaignId ?? null,
    campaignName: eligibility.campaign_name ?? eligibility.campaignName ?? '',
    triggerType,
    triggerTypeLabel: labelFor(TRIGGER_TYPE_LABELS, triggerType),
    matchedAt,
    matchedAtLabel: formatCampaignDate(matchedAt),
    status,
    statusLabel: labelFor(CAMPAIGN_STATUS_LABELS, status),
    proStatus,
    proStatusLabel: labelFor(PRO_STATUS_LABELS, proStatus),
  };
};

// ---------------------------------------------------------------------------
// Promo codes (GOTU-5661 list + detail, GOTU-6663 office view)
// ---------------------------------------------------------------------------

/**
 * An instant as `MM/DD/YY` in the admin's timezone — the format every date on
 * the Promo Codes mockups uses. `startsAt`/`endsAt` are timestamps, not
 * calendar dates, so building a Date is right here where it would be wrong
 * for awards-service's `YYYY-MM-DD` fields.
 */
export const formatPromoDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return EMPTY_VALUE;

  const month = String(instant.getMonth() + 1).padStart(2, '0');
  const day = String(instant.getDate()).padStart(2, '0');

  return `${month}/${day}/${String(instant.getFullYear()).slice(-2)}`;
};

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * The discount as the mockups show it: "50%" for a percent code.
 *
 * Fixed-amount codes became applyable in campaign-service #74 and render as
 * dollars ("$25.00") — the service stores `value` in cents for that kind, and
 * rendering it as "0%" on the surface an admin opens to check a code would be
 * exactly wrong. A spec that does not decode is an em dash, matching the
 * service's own choice of `discount: null` over a fabricated zero.
 *
 * Accepts either the campaign's `mechanismSpec` ({ discountKind, value,
 * maxCents }) or the office history's `discount` ({ kind, value, maxAmount }):
 * one column, two spellings on the wire.
 */
export const formatDiscount = (spec) => {
  if (!spec) return EMPTY_VALUE;

  const kind = spec.discountKind ?? spec.kind;
  const value = Number(spec.value);
  if (!Number.isFinite(value)) return EMPTY_VALUE;

  if (kind === 'percent') return `${value}%`;
  if (kind === 'fixed') return usd.format(value / 100);

  return EMPTY_VALUE;
};

/**
 * A quota cap as the ticket wants it: the number, or "0" when uncapped.
 *
 * The wire distinguishes "no cap" (null/absent) from "a cap of zero", but the
 * ticket collapses both to 0 for the readout. `EMPTY_VALUE` is reserved for a
 * cap we could not read at all.
 */
export const formatMaxUses = (cap) => {
  if (cap === null || cap === undefined) return '0';

  const count = Number(cap);

  return Number.isFinite(count) ? String(count) : EMPTY_VALUE;
};

export const AUTO_APPLY_LABELS = { true: 'Yes', false: 'No' };

// The targeting keys campaign-service's catalog defines for an office-identity
// campaign, mapped onto the lists the detail page reads. Anything else in a
// rule — postal codes, thresholds — has no column on the design and is not
// carried through.
//
// `office_id` (GOTU-8105) is collected only so the page knows whether to show
// the Office List row. A rule may name up to 10,000 offices, so the viewer
// reads them — with names, paged and searchable — from `/target-offices`
// rather than from these ids.
const TARGETING_LISTS = {
  shift_profession: 'professions',
  posting_type: 'postingTypes',
  office_address_state: 'states',
  office_metro: 'metroIds',
  office_dso_code: 'dsoCodes',
  office_id: 'officeIds',
};

// `shift` and `interview` are the whole set campaign-service accepts for
// `posting_type`; the design reads them out in the plural.
const POSTING_TYPE_LABELS = {
  shift: 'Shifts',
  interview: 'Interviews',
};

const leafValues = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value === null || value === undefined) return [];

  return [String(value)];
};

/**
 * Flattens a targeting-rule tree into the "included" lists the page reads:
 * profession types, posting types, states, metro ids, DSOs and office ids —
 * each empty when the rule does not constrain it. The page renders an empty
 * profession, state or DSO list as "All", and leaves the Type, Metro and
 * Office List rows out entirely.
 *
 * The tree is `{and:[…]}` / `{or:[…]}` branches over `{key, operator, value}`
 * leaves (or `{matchAll: true}`). This readout deliberately ignores the
 * boolean structure: it answers "which values does this rule mention" rather
 * than "exactly who matches", which is what a look-only V2 needs and all the
 * design has room for. Only `eq` and `in` leaves contribute — a negation or
 * range operator on these keys would need a richer readout than a list.
 *
 * Values are de-duplicated and sorted so the readout is stable across saves.
 */
export const flattenTargetingRule = (rule) => {
  const lists = {
    professions: [], postingTypes: [], states: [], metroIds: [], dsoCodes: [], officeIds: [],
  };

  const walk = (node) => {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node.and)) node.and.forEach(walk);
    if (Array.isArray(node.or)) node.or.forEach(walk);

    const list = TARGETING_LISTS[node.key];
    if (list && (node.operator === 'eq' || node.operator === 'in')) {
      lists[list].push(...leafValues(node.value));
    }
  };

  walk(rule);

  return Object.fromEntries(
    Object.entries(lists).map(([name, values]) => [name, [...new Set(values)].sort()]),
  );
};

/**
 * One campaign from `GET /v1/admin/campaigns` (or `/:id`) as a promo-code row.
 *
 * `status` is the ticket's date-derived status (see derivePromoCodeStatus),
 * not the lifecycle value, which is kept alongside as `campaignStatus`.
 *
 * `maxUses` is the per-office cap (`quotaCaps.perIdentity`). That is the cap
 * nearly every code in the data actually sets, it is the figure the office
 * tab calls "Max Uses" too, and it is what an admin answering "how many times
 * can this office use it" needs; rendering the campaign-wide cap there read
 * as "unlimited" for codes that were capped. The campaign-wide cap
 * (`quotaCaps.total`) is carried separately for the detail page.
 *
 * Metro targets are RAM ids on the rule; their names come from
 * `targetingLabels.office_metro` on the same response (GOTU-9611). An id with
 * no name there — or a response from a build that predates the field — falls
 * back to the raw id rather than dropping the metro from the readout.
 */
export const mapPromoCode = (campaign = {}, now = Date.now()) => {
  const status = derivePromoCodeStatus(
    {
      campaignStatus: campaign.status,
      startsAt: campaign.startsAt,
      endsAt: campaign.endsAt,
    },
    now,
  );
  const autoApply = Boolean(campaign.autoApply);
  const quotaCaps = campaign.quotaCaps ?? {};
  const targeting = flattenTargetingRule(campaign.targetingRule);
  const metroNames = campaign.targetingLabels?.office_metro ?? {};

  return {
    id: campaign.id,
    name: campaign.name ?? '',
    code: campaign.promoCode ?? '',
    campaignStatus: campaign.status ?? null,
    status,
    statusLabel: PROMO_CODE_STATUS_LABELS[status] ?? titleCaseToken(status),
    discountLabel: formatDiscount(campaign.mechanismSpec),
    startsAt: campaign.startsAt ?? null,
    startDateLabel: formatPromoDate(campaign.startsAt),
    endsAt: campaign.endsAt ?? null,
    endDateLabel: formatPromoDate(campaign.endsAt),
    maxUses: quotaCaps.perIdentity ?? null,
    maxUsesLabel: formatMaxUses(quotaCaps.perIdentity),
    campaignMaxUses: quotaCaps.total ?? null,
    campaignMaxUsesLabel: formatMaxUses(quotaCaps.total),
    autoApply,
    autoApplyLabel: AUTO_APPLY_LABELS[autoApply],
    targeting: {
      ...targeting,
      postingTypeLabels: targeting.postingTypes.map((type) => labelFor(POSTING_TYPE_LABELS, type)),
      metros: targeting.metroIds.map((id) => ({ id, name: metroNames[id] ?? id })),
    },
    createdAt: campaign.createdAt ?? null,
  };
};

/**
 * One item of `GET /v1/admin/campaigns/:id/target-offices` as a row in the
 * Office List viewer.
 *
 * campaign-service sends `name: ""` for an office its identity mirror has not
 * seen yet. The ticket shows the raw id there instead, so `displayName` is
 * never blank.
 */
export const mapTargetOffice = (item = {}) => {
  const officeId = item.officeId ?? '';
  const name = item.name ?? '';

  return { officeId, name, displayName: name || officeId };
};

/**
 * One entry of `GET /v1/admin/offices/:officeId/discounts` as a row on the
 * office's Discounts & Promo Codes tab.
 *
 * `applied` is the ticket's office-level flag: the code reached this office
 * either by being entered (an opt-in exists) or by an automatic campaign
 * granting it directly (a redemption with no opt-in). False is a real state —
 * since campaign-service #123 (GOTU-5660) the history also carries every live
 * campaign the office currently matches but holds neither an opt-in nor a
 * redemption for, which is exactly the "Applied: No" row.
 *
 * `codeLabel` is the code an office types to redeem the discount, em dash when
 * the campaign carries none. Empty is not the same question as `automatic`:
 * since GOTU-6587 an auto-applying campaign has a code *and* was entered by the
 * batch, so read `automatic` for that and never infer it from an empty code.
 *
 * `usedCount` excludes freed redemptions: a cancelled or failed one took no
 * quota, which is the same rule the service's own `usesRemaining` applies.
 * `maxUses` is reconstructed from used + remaining because the per-office cap
 * is not on this response; null `usesRemaining` means uncapped, which the
 * ticket renders as 0.
 */
export const mapOfficeDiscount = (entry = {}, now = Date.now()) => {
  const status = derivePromoCodeStatus(
    {
      campaignStatus: entry.campaignStatus,
      startsAt: entry.startsAt,
      endsAt: entry.endsAt,
    },
    now,
  );
  const redemptions = Array.isArray(entry.redemptions) ? entry.redemptions : [];
  const usedCount = redemptions.filter((r) => r.quotaState !== 'freed').length;
  const applied = Boolean(entry.optInId) || redemptions.length > 0;
  const maxUses = entry.usesRemaining === null || entry.usesRemaining === undefined
    ? null
    : usedCount + Number(entry.usesRemaining);

  return {
    campaignId: entry.campaignId,
    campaignName: entry.campaignName ?? '',
    code: entry.code ?? '',
    // `||`, not `??`: the wire type is a non-nullable string, so "no code"
    // arrives as '' rather than null.
    codeLabel: entry.code || EMPTY_VALUE,
    automatic: Boolean(entry.automatic),
    campaignStatus: entry.campaignStatus ?? null,
    status,
    statusLabel: PROMO_CODE_STATUS_LABELS[status] ?? titleCaseToken(status),
    live: Boolean(entry.live),
    officeEligible: Boolean(entry.officeEligible),
    discountLabel: formatDiscount(entry.discount),
    startDateLabel: formatPromoDate(entry.startsAt),
    endDateLabel: formatPromoDate(entry.endsAt),
    applied,
    appliedLabel: applied ? 'Yes' : 'No',
    appliedByName: entry.appliedByName ?? '',
    usedCount,
    usedCountLabel: String(usedCount),
    maxUses,
    maxUsesLabel: formatMaxUses(maxUses),
  };
};
